import React, { useState } from 'react'
import {
  useMediaQuery,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  createTheme,
  Box,
  Popover,
} from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import MenuIcon from '@mui/icons-material/Menu'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import QueryDialog from './QueryDialog'
import {
  Quiz as QuizIcon,
  PeopleOutline as PeopleOutlineIcon,
  CancelOutlined,
  DoneAllOutlined,
} from '@mui/icons-material'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import DescriptionIcon from '@mui/icons-material/Description'
import ProcessTimeline from '../../../components/Dashboard/ProcessTimeline'
import moment from 'moment'
import { ACTIVITY_TYPES, FIELD_TYPES } from '../../../utility/constants'
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import {
  useAllValues,
  usePageDetails,
  usePreviousProcessData,
  useProcessContext,
  useReviewValues,
  useTableStructure,
} from '../../../store'
import {
  getLegacySubfieldReviewId,
  getPageId,
  getReviewId,
  getTableFieldId,
  getTableReviewId,
  getTableRowId,
} from '../utils/utils'
import AdditionalNotes from './AdditionalNotes'
import GenerateDocumentsDialog from './GenerateDocumentsDialog'
import { getTimeInMillis } from '../../../utility/datetime'
import { useResponsiveTypography } from '../../../utility/responsiveTypography'

import DataPreview from '../../../components/DataPreview/DataPreview'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import { getTableRowFromLegacyAllValues } from '../utils/tableFieldUtil'

const theme = createTheme()

export const REVIEW_SCOPE_PAGE = 'page'

const DataEntryPageNav = ({
  pages,
  processId,
  setCurrentPageIndex,
  currentPageIndex,
  processTemplateId,
  processReferenceNum,
  isPageReady = false,
  processTemplate,
}) => {
  const [isQueryDialogOpen, setIsQueryDialogOpen] = useState(false)
  const [isAdditionalNoteDialogue, setIsAdditionalNoteDialogue] = useState(false)
  const [isGenerateDocumentDialogue, setIsGenerateDocumentDialogue] = useState(false)

  const [anchorEl, setAnchorEl] = useState(null)
  const [actionType, setActionType] = useState(null)

  const reviewValues = useReviewValues((state) => state.reviewValues)
  const setReviewValues = useReviewValues((state) => state.setReviewValues)

  const dataEntryPageTittle = useResponsiveTypography()

  const previousProcessDataValues = usePreviousProcessData((state) => state.previousProcessData)
  const [selectedPreviewProcessId, setSelectedPreviewProcessId] = useState(null)
  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getAllValues = useAllValues((state) => state.getAllValues)

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  const handleOpenPopover = (event, type) => {
    setAnchorEl(event.currentTarget)
    setActionType(type)
  }

  const handleClosePopover = () => {
    setAnchorEl(null)
    setActionType(null)
  }

  const pageDetails = usePageDetails((state) => state.pageDetails)

  const tableStructure = useTableStructure((state) => state.tableStructure)

  const handleAcceptRejectAll = (accepted = false, scope = REVIEW_SCOPE_PAGE) => {
    let fields =
      scope === REVIEW_SCOPE_PAGE
        ? pageDetails[getPageId(pages[currentPageIndex]?.id)]
        : processContext?.allFields

    fields = fields.filter((it) => {
      if (it.data_structure === FIELD_TYPES.TABLE) {
        return true
      } else {
        const isReviewableField = Object.keys(reviewValues).find(
          (key) => !key.includes('__') && key.split('_')[1] == it.id
        )

        return !!isReviewableField
      }
    })
    let updatedReviewValues = {}

    fields.forEach((field) => {
      if (field.data_structure === FIELD_TYPES.TABLE) {
        let columns = []
        let rows = []

        const tableStructureValues = tableStructure[getTableFieldId(field.id)]
        if (tableStructureValues) {
          // getting table structure from store
          columns = tableStructureValues.columns
          rows = tableStructureValues.rows
        } else {
          // generating table structure on the fly
          columns = processContext?.tableSubfields?.find((it) => it.id === field.id)?.subfields
          const allValues = getAllValues()
          rows = getTableRowFromLegacyAllValues(allValues, columns)
        }

        columns = columns.filter((it) => {
          const isReviewableField = Object.keys(reviewValues).find(
            (key) => key.includes('__') && key.split('__')[0].split('_')[1] == it.id
          )

          return !!isReviewableField
        })

        let tableData = {}
        rows?.forEach((row) => {
          let rowData = {}
          columns?.forEach((col) => {
            if (getLegacySubfieldReviewId(col.id, row) in reviewValues) {
              updatedReviewValues = {
                ...updatedReviewValues,
                [getTableReviewId(col.id, row)]: accepted,
              }
              rowData = {
                ...rowData,
                [getReviewId(col.id)]: accepted,
              }
            }
          })
          tableData = {
            ...tableData,
            [getTableRowId(row)]: { ...rowData, updatedAt: getTimeInMillis() },
          }
        })
        updatedReviewValues = {
          ...updatedReviewValues,
          [getTableFieldId(field.id)]: tableData,
          updatedAt: getTimeInMillis(),
        }
      } else {
        if (!field.is_read_only && !field.is_subfield && getReviewId(field.id) in reviewValues) {
          updatedReviewValues = {
            ...updatedReviewValues,
            [getReviewId(field.id)]: accepted,
            updatedAt: getTimeInMillis(),
          }
        }
      }
    })

    setReviewValues({ ...reviewValues, ...updatedReviewValues })
  }

  const handleOptionSelect = (scope) => {
    handleAcceptRejectAll(actionType === 'accept', scope)
    handleClosePopover()
  }

  const renderPageStatus = () => {
    return (
      <>
        <IconButton
          className="dataentry-headericon"
          style={{
            height: 30,
            width: 30,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px solid #E1D9FB',
            paddingLeft: 15,
            backgroundColor: '#F4F1FF',
          }}
          onClick={() => {
            setCurrentPageIndex(currentPageIndex > 0 ? currentPageIndex - 1 : 0)
          }}
          disabled={!isPageReady}
        >
          <ArrowBackIosIcon
            sx={{
              width: '16px',
              height: '16px',
            }}
          />
        </IconButton>
        <Typography
          className="entryheading"
          style={{
            ...dataEntryPageTittle,
            fontWeight: 'bold',
            fontSize: '13px',
          }}
        >
          Page {currentPageIndex + 1} of {pages?.length}:{' '}
          {pages[currentPageIndex]?.page_template_name}
        </Typography>

        <IconButton
          style={{
            height: 30,
            width: 30,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px solid #E1D9FB',
            backgroundColor: '#F4F1FF',
          }}
          onClick={() => {
            setCurrentPageIndex(
              currentPageIndex < pages?.length - 1 ? currentPageIndex + 1 : pages?.length - 1
            )
          }}
          disabled={!isPageReady}
        >
          <ArrowForwardIosIcon
            sx={{
              width: '16px',
              height: '16px',
            }}
          />
        </IconButton>
      </>
    )
  }

  const renderPrevProcessData = () => (
    <>
      {previousProcessDataValues?.prevDataEntryProcessData && (
        <div style={{ display: 'flex', columnGap: '10px' }}>
          <div>Data entered by:</div>
          <div>
            {previousProcessDataValues?.prevDataEntryProcessData?.first_name +
              ' ' +
              previousProcessDataValues?.prevDataEntryProcessData?.last_name}
            <br />
            {' (' + previousProcessDataValues?.prevDataEntryProcessData?.email + ')'}
            <br />
            {moment(
              new Date(previousProcessDataValues?.prevDataEntryProcessData?.modified_at)
            ).format('DD MMM YYYY, hh:mm A')}
          </div>
        </div>
      )}
      {previousProcessDataValues?.prevReviewProcessData && (
        <div style={{ display: 'flex', columnGap: '10px' }}>
          <div>Data reviewed by:</div>
          <div>
            {previousProcessDataValues?.prevReviewProcessData?.first_name +
              ' ' +
              previousProcessDataValues?.prevReviewProcessData?.last_name}
            <br />
            {' (' + previousProcessDataValues?.prevReviewProcessData?.email + ')'}
            <br />
            {moment(new Date(previousProcessDataValues?.prevReviewProcessData?.modified_at)).format(
              'DD MMM YYYY, hh:mm A'
            )}
          </div>
        </div>
      )}
    </>
  )

  return (
    <>
      {pages?.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              columnGap: 10,
              alignItems: 'center',
            }}
          >
            {/* page status */}
            {renderPageStatus()}
            {/* preview */}
            {isMobile ? (
              <>
                <IconButton onClick={toggleDrawer} size="small">
                  <MenuIcon />
                </IconButton>
                <Drawer anchor="right" open={isDrawerOpen} onClose={toggleDrawer}>
                  <Box width={250} pt={6}>
                    <List>
                      <ListItem>
                        <Box>
                          <Tooltip title={'Data Preview'}>
                            <IconButton
                              size="small"
                              onClick={() => setSelectedPreviewProcessId(processId)}
                            >
                              <VisibilityOutlinedIcon color={'info'} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <ListItemText style={{ paddingLeft: '20px' }} primary="Data Preview" />
                      </ListItem>
                      {processTemplateId && (
                        <ListItem>
                          <Box>
                            <ProcessTimeline
                              processId={processId}
                              processReferanceNum={processReferenceNum}
                              processTemplateId={processTemplateId}
                              templateRoutes={processContext?.processAllRoutes}
                              processTemplates={processTemplate}
                            />
                          </Box>
                          <ListItemText style={{ paddingLeft: '20px' }} primary="Timeline Data" />
                        </ListItem>
                      )}
                      <ListItem button onClick={() => setIsQueryDialogOpen(true)}>
                        <ListItemIcon>
                          <QuizOutlinedIcon style={{ fontSize: '18px' }} />
                        </ListItemIcon>
                        <ListItemText primary="Queries" />
                      </ListItem>
                      <ListItem button onClick={() => setIsAdditionalNoteDialogue(true)}>
                        <ListItemIcon>
                          <NoteAddOutlinedIcon style={{ fontSize: '18px' }} />
                        </ListItemIcon>
                        <ListItemText primary="Additional Notes" />
                      </ListItem>
                      <ListItem button onClick={() => setIsGenerateDocumentDialogue(true)}>
                        <ListItemIcon>
                          <DescriptionOutlinedIcon style={{ fontSize: '18px' }} />
                        </ListItemIcon>
                        <ListItemText primary="Preview document(s)" />
                      </ListItem>
                    </List>
                  </Box>
                </Drawer>
              </>
            ) : (
              <>
                <>
                  {/* First eye icon */}
                  <Tooltip title={'Data Preview'}>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedPreviewProcessId(processId)}
                      sx={{ padding: '2px' }}
                    >
                      <VisibilityOutlinedIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                  </Tooltip>
                </>

                {processTemplateId && processContext?.processAllRoutes && (
                  // Second info incon
                  <ProcessTimeline
                    processId={processId}
                    processReferanceNum={processReferenceNum}
                    processTemplateId={processTemplateId}
                    templateRoutes={processContext?.processAllRoutes}
                    processTemplates={processTemplate}
                  />
                )}
                {(previousProcessDataValues?.prevDataEntryProcessData ||
                  previousProcessDataValues?.prevReviewProcessData) && (
                  <>
                    {activityType != ACTIVITY_TYPES.DATA_ENTRY && (
                      <div>
                        <Tooltip title="Reject all data on this page">
                          <IconButton
                            sx={{ padding: '2px' }}
                            size="small"
                            onClick={(event) => handleOpenPopover(event, 'reject')}
                          >
                            <CancelOutlined size="small" sx={{ fontSize: '18px' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Accept all data on this page">
                          <IconButton
                            sx={{ padding: '2px', position: 'relative', left: '6px' }}
                            size="small"
                            onClick={(event) => handleOpenPopover(event, 'accept')}
                          >
                            <DoneAllOutlined size="small" sx={{ fontSize: '18px' }} />
                          </IconButton>
                        </Tooltip>

                        <Popover
                          open={Boolean(anchorEl)}
                          anchorEl={anchorEl}
                          onClose={handleClosePopover}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                        >
                          <List>
                            {actionType === 'reject' ? (
                              <>
                                <ListItem
                                  button
                                  onClick={() => handleOptionSelect(REVIEW_SCOPE_PAGE)}
                                >
                                  <ListItemText
                                    style={{ padding: '0px 10px 0px' }}
                                    sx={{ fontSize: '12px' }}
                                    primary={
                                      <Typography style={{ fontSize: '12px' }}>
                                        Reject all fields on this page
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                                <ListItem
                                  style={{ padding: '0px 10px 0px' }}
                                  button
                                  onClick={() => handleOptionSelect('task')}
                                >
                                  <ListItemText
                                    sx={{ fontSize: '12px' }}
                                    primary={
                                      <Typography style={{ fontSize: '12px' }}>
                                        Reject all fields in task
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              </>
                            ) : (
                              <>
                                <ListItem
                                  style={{ padding: '0px 10px 0px' }}
                                  button
                                  onClick={() => handleOptionSelect(REVIEW_SCOPE_PAGE)}
                                >
                                  <ListItemText
                                    sx={{ fontSize: '24px' }}
                                    primary={
                                      <Typography style={{ fontSize: '12px' }}>
                                        Approve all fields on this page
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                                <ListItem
                                  style={{ padding: '0px 10px 0px' }}
                                  button
                                  onClick={() => handleOptionSelect('task')}
                                >
                                  <ListItemText
                                    sx={{ fontSize: '24px' }}
                                    primary={
                                      <Typography style={{ fontSize: '12px' }}>
                                        Approve all fields in task
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                                {/* <ListItem>
                                  <Tooltip title={renderPrevProcessData()}>
                                    <ListItemIcon>
                                      <PeopleOutlineIcon style={{ fontSize: '18px' }} />
                                    </ListItemIcon>
                                  </Tooltip>
                                  <ListItemText primary="Data reviewed by" />
                                </ListItem> */}
                              </>
                            )}
                          </List>
                        </Popover>
                      </div>
                    )}

                    <Tooltip title={renderPrevProcessData()}>
                      <IconButton size="small" sx={{ padding: '2px' }}>
                        <PeopleOutlineIcon size="small" sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Queries">
                  <IconButton
                    onClick={() => setIsQueryDialogOpen(true)}
                    size="small"
                    sx={{ padding: '2px' }}
                  >
                    {/* Queries icon */}
                    <QuizOutlinedIcon size="small" sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Additional Notes">
                  <IconButton
                    onClick={() => setIsAdditionalNoteDialogue(true)}
                    size="small"
                    sx={{ padding: '2px' }}
                  >
                    {/* Additional note icon */}
                    <NoteAddOutlinedIcon size="small" sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
                {processContext.hasDocuments && (
                  <Tooltip title="Preview document(s)">
                    <IconButton
                      onClick={() => setIsGenerateDocumentDialogue(true)}
                      size="small"
                      sx={{ padding: '2px' }}
                    >
                      <DescriptionOutlinedIcon size="small" sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </>
      )}

      {isGenerateDocumentDialogue && (
        <GenerateDocumentsDialog
          isGenerateDocumentDialogue={isGenerateDocumentDialogue}
          setIsGenerateDocumentDialogue={setIsGenerateDocumentDialogue}
          processTemplateId={processTemplateId}
        />
      )}

      {isAdditionalNoteDialogue && (
        <AdditionalNotes
          processId={processId}
          isAdditionalNoteDialogue={isAdditionalNoteDialogue}
          setIsAdditionalNoteDialogue={setIsAdditionalNoteDialogue}
          processReferenceNum={processReferenceNum}
          processTemplateId={processTemplateId}
        />
      )}
      {isQueryDialogOpen && (
        <QueryDialog
          processId={processId}
          isQueryDialogOpen={isQueryDialogOpen}
          setIsQueryDialogOpen={setIsQueryDialogOpen}
        />
      )}

      {selectedPreviewProcessId ? (
        <DataPreview
          onClose={() => setSelectedPreviewProcessId(null)}
          processId={selectedPreviewProcessId}
          open={!!selectedPreviewProcessId}
        />
      ) : null}
    </>
  )
}

export default DataEntryPageNav
